import {Context, context, ContextType} from './context'
import {langEn} from './lang-en'
import {langRu} from './lang-ru'

export type Language = 'en' | 'ru'

export type LangType = typeof langEn

export const isLanguage = (v: unknown): v is Language =>
  typeof v === 'string' && (v === 'en' || v === 'ru')
export const asLanguage = (v: unknown): Language => (isLanguage(v) ? v : 'ru')

export class Lang {
  private static currentLang: Language = 'en'
  private static handlers: Set<() => void> = new Set()

  private static supported: {[key: string]: LangType} = {
    en: langEn,
    ru: langRu
  }

  private static isInitialized = false

  public static msg = langEn

  private static contextPredicate: (() => ContextType | undefined) | undefined =
    undefined
  public static setContextPredicate = (
    predicate: typeof Lang.contextPredicate
  ): void => {
    Lang.contextPredicate = predicate
  }

  public static get context(): Context {
    const contextType = this.contextPredicate?.() ?? 'default'

    const contextData = context[contextType] ?? context.default
    return contextData[Lang.currentLang]
  }

  public static localName(language: Language): string {
    return Lang.supported[language]?.langName || ''
  }

  public static getMessages(lang: Language): typeof langEn {
    if (lang === 'ru') {
      return langRu
    } else {
      return langEn
    }
  }

  public static isEqualToMessageByKey(str: string, key: string): boolean {
    return (
      str === (langEn as {[k: string]: any})[key] ||
      str === (langRu as {[k: string]: any})[key]
    )
  }

  public static getPreferred(): Language {
    // todo: remove this tmp fix (use geolocation to improve results)
    return 'ru'
    /*
    const nav: Navigator & {userLanguage?: string} = navigator
    const langCode =
      (nav.languages?.[0] ? nav.languages[0] : nav.language) || nav.userLanguage
    if (langCode === 'ru' || (!!langCode && langCode.indexOf('ru') === 0)) {
      return 'ru'
    } else {
      return 'en'
    }
    */
  }

  public static findMsg(key: string): string {
    const msg = Lang.msg as any
    if (msg[key]) {
      return msg[key]
    } else {
      // eslint-disable-next-line no-console
      console.error('Message string not found: ' + key)
      return '???'
    }
  }

  public static findPartsMsg(part: string, key: string): string {
    const msg = Lang.msg as any
    if (msg[part][key]) {
      return msg[part][key]
    } else {
      // eslint-disable-next-line no-console
      console.error(`Message string not found: ${part}.${key}`)
      return '???'
    }
  }

  public static addLangChangeHandler(handler: () => void): void {
    Lang.handlers.add(handler)
  }

  public static removeLangChangeHandler(handler: () => void): void {
    Lang.handlers.delete(handler)
  }

  public static getLang(): Language {
    return Lang.currentLang
  }

  private static setIsInitialized(value: boolean): void {
    Lang.isInitialized = value
  }

  static getIsInitialized(): boolean {
    return Lang.isInitialized
  }

  public static setLang(l: Language): void {
    const was = Lang.currentLang
    if (!Lang.supported[l]) {
      // eslint-disable-next-line no-console
      console.error('Unsupported language, setting default')
      Lang.currentLang = 'en'
    } else {
      Lang.currentLang = l
    }
    Lang.msg = Lang.supported[Lang.currentLang]
    if (was !== Lang.currentLang) {
      Lang.handlers.forEach((h: () => void) => h())
    }

    Lang.setIsInitialized(true)
  }

  static getLanguages() {
    const languages: Language[] = ['en', 'ru']
    return languages.map(lang => {
      return {
        langName: Lang.supported[lang]?.langName || '',
        langCode: lang
      }
    })
  }

  static tryLocalize(key: string): string {
    if (key in langEn) {
      const lang = Lang.getLang()
      const msg = Lang.getMessages(lang)
      const res = msg[key as keyof typeof langEn]
      return typeof res === 'string' ? res : key
    } else {
      return key
    }
  }
}
